
import {
  PropType, defineComponent, computed, reactive, ref,
} from '@vue/composition-api';
import { format as d3Format } from 'd3-format';
import { colors } from '../../utils/constants';
import { textColor } from '../../utils';

export interface Item {
  Metabolite: string;
  color: string;
  [key: string]: number | string;
}

interface Header {
  text: string;
  align?: string;
  value: string;
  isLabel: boolean;
}

export default defineComponent({
  props: {
    items: {
      type: Array as PropType<Item[]>,
      required: true,
    },
    value: {
      type: Array as PropType<string[]>,
      required: true,
    },
    headers: {
      type: Array as PropType<Header[]>,
      required: true,
    },
    threshold: {
      type: Number,
      default: 0.05,
    },
    noDataAvailableMsg: { // allows displaying a custom message when table has no data.
      type: String,
      default: 'No data available',
    },
  },

  setup(props, { emit }) {
    const format = ref(d3Format('.4e'));
    const pagination = reactive({ rowsPerPage: -1 });
    const selectedItems = computed<Item[]>({
      get() {
        const s = new Set(props.value);
        return props.items.filter((d) => s.has(d.Metabolite));
      },
      set(newValue) {
        const s = newValue.map((d) => d.Metabolite);
        emit('input', s);
      },
    });

    function isInteresting(value: number) {
      return value < props.threshold;
    }
    function toggleHighlighted(header: Header, evt: Event, add: boolean) {
      evt.preventDefault();
      evt.stopPropagation();

      const highlighted = props.items.filter((item) => isInteresting(item[header.value] as number));
      const current = selectedItems.value.slice();
      const currentLookup = new Set(current);
      highlighted.forEach((item) => {
        if (add && !currentLookup.has(item)) {
          current.push(item);
        } else if (!add && currentLookup.has(item)) {
          const index = current.indexOf(item);
          current.splice(index, 1);
        }
      });
      selectedItems.value = current;
    }
    function cellStyle(item: Item) {
      if (!item.color) {
        return null;
      }
      return {
        backgroundColor: item.color,
        color: textColor(item.color),
      };
    }
    return {
      format,
      pagination,
      colors,
      selectedItems,
      isInteresting,
      toggleHighlighted,
      cellStyle,
    };
  },
});
